import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import { Card, CardContent } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { 
  ArrowLeftIcon, 
  CheckCircle2Icon, 
  AlertCircleIcon, 
  PencilIcon,
  BotIcon,
  UserIcon,
  UsersIcon,
  CalendarIcon
} from "lucide-react";
import ReviewItemsTable from "./ReviewItemsTable";
import ReviewDiffModal from "./ReviewDiffModal";
import moment from "moment";
import { cn } from "../../../lib/utils";
import { FinetuneDataset } from "../../../model_gen/finetuning";

const ReviewDetail: React.FC = () => {
  const { datasetId, reviewId } = useParams();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();

  const [review, setReview] = useState<any>(null);
  const [dataset, setDataset] = useState<FinetuneDataset | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [diffModalState, setDiffModalState] = useState<{isOpen: boolean, item: any | null}>({isOpen: false, item: null});
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [reviewProgress, setReviewProgress] = useState<any>(null);

  // Fetch Dataset Details
  useEffect(() => {
    if (!datasetId) return;

    const sub = DatasetService.getDataset(
      Number(datasetId),
      undefined,
      apiKey
    ).subscribe(
      (data: FinetuneDataset) => {
        setDataset(data);
      },
      (err) => {
        console.error("Error fetching dataset:", err);
      }
    );

    return () => sub.unsubscribe();
  }, [datasetId, apiKey]);

  // Fetch Review Details
  useEffect(() => {
    if (!reviewId) return;

    const sub = DatasetService.getDatasetReview(
      Number(reviewId),
      undefined,
      apiKey
    ).subscribe(
      (data) => {
        setReview(data);
        setIsLoading(false);
      },
      (err) => {
        console.error("Error fetching review:", err);
        setError("Failed to load review details.");
        setIsLoading(false);
      }
    );

    return () => sub.unsubscribe();
  }, [reviewId, apiKey]);

  // Fetch Review Progress
  useEffect(() => {
    if (!datasetId) return;

    const sub = DatasetService.getDatasetReviewProgressByDataset(
      Number(datasetId),
      null,
      apiKey
    ).subscribe(
      (data) => {
        if (data && reviewId && data[reviewId]) {
          setReviewProgress(data[reviewId]);
        }
      },
      (err) => {
        console.error("Error fetching review progress:", err);
      }
    );

    return () => sub.unsubscribe();
  }, [datasetId, reviewId, apiKey]);

  // Fetch Review Items
  useEffect(() => {
    if (!reviewId) return;

    setIsItemsLoading(true);
    const offset = (currentPage - 1) * pageSize;

    const sub = DatasetService.getDatasetReviewItems(
      Number(reviewId),
      offset,
      pageSize,
      setIsItemsLoading,
      apiKey
    ).subscribe(
      (response: { items: any[]; total: number }) => {
        setItems(response.items || []);
        setTotalItems(response.total);
      },
      (err) => {
        console.error("Error fetching review items:", err);
      }
    );

    return () => sub.unsubscribe();
  }, [reviewId, currentPage, pageSize, apiKey]);

  const getReviewTypeIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case "human":
      case "expert":
        return <UserIcon className="w-4 h-4" />;
      case "mixed":
        return <UsersIcon className="w-4 h-4" />;
      case "ai":
      default:
        return <BotIcon className="w-4 h-4" />;
    }
  };

  const formatReviewType = (type: string) => {
    if (!type) return "AI";
    switch (type.toLowerCase()) {
      case "ai":
        return "AI";
      case "human":
        return "Human";
      case "expert":
        return "Expert";
      case "mixed":
        return "Mixed";
      default:
        return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "COMPLETE":
      case "COMPLETED":
        return "text-green-600 bg-green-50 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800";
      case "FAILED":
      case "ERROR":
        return "text-red-600 bg-red-50 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800";
      case "PROCESSING":
      case "RUNNING":
        return "text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="h-48 bg-muted animate-pulse rounded-lg" />
          <div className="h-96 bg-muted animate-pulse rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="min-h-screen bg-background p-8 flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-red-500">{error || "Review not found"}</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border overflow-hidden">
          <CardContent className="p-6 md:p-8">
            {/* Breadcrumb */}
            <nav className="mb-5">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <Link
                    to="/forge/compositions?tab=datasets"
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Datasets
                  </Link>
                </li>
                <li className="text-muted-foreground/40">/</li>
                <li>
                  <Link
                    to={`/forge/datasets/${datasetId}`}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    {dataset?.name || `Dataset #${datasetId}`}
                  </Link>
                </li>
                <li className="text-muted-foreground/40">/</li>
                <li>
                  <span className="text-maitai-green font-gc-med">
                    This Review
                  </span>
                </li>
              </ol>
            </nav>

            {/* Title & Status */}
            <div className="flex items-start justify-between mb-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl md:text-3xl font-gc-demi-bold text-foreground">
                    Review Details
                  </h1>
                  <Badge 
                    variant="outline" 
                    className={cn("capitalize px-2.5 py-0.5 text-xs font-gc-med", getStatusColor(review.status))}
                  >
                    {review.status.toLowerCase()}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground font-gc-reg">
                  <div className="flex items-center gap-1.5 bg-muted/50 px-2.5 py-1 rounded-full">
                    {getReviewTypeIcon(review.review_type)}
                    <span>{formatReviewType(review.review_type)} Review</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CalendarIcon className="w-4 h-4" />
                    <span>Started {moment(review.date_created).format("MMM D, YYYY [at] h:mm:ss A")}</span>
                  </div>
                  {review.date_completed && (
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2Icon className="w-4 h-4 text-green-500" />
                      <span>Completed {moment(review.date_completed).format("MMM D, YYYY [at] h:mm:ss A")}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <Button 
                variant="outline" 
                onClick={() => navigate(`/forge/datasets/${datasetId}?tab=reviews`)}
                className="gap-2 shrink-0"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to Reviews
              </Button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2Icon className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground font-gc-reg">Total Reviewed</p>
                    </div>
                    <p className="text-3xl font-gc-demi-bold text-foreground">
                      {review.num_reviews || 0}
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <PencilIcon className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground font-gc-reg">Corrections</p>
                    </div>
                    <p className="text-3xl font-gc-demi-bold text-foreground">
                      {review.num_corrections || 0}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircleIcon className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground font-gc-reg">Pending / Errors</p>
                    </div>
                    <p className="text-3xl font-gc-demi-bold text-foreground">
                      {reviewProgress ? reviewProgress.error_count : 0}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Content Card */}
        <Card className="border-border overflow-hidden">
          <CardContent className="p-0">
             <div className="px-6 py-4 border-b border-border bg-muted/20">
               <h3 className="text-base font-gc-med text-foreground">Review Items</h3>
               <p className="text-sm text-muted-foreground mt-0.5">
                 {totalItems} request{totalItems !== 1 ? "s" : ""} in this review
               </p>
             </div>
             <div className="p-4">
               <ReviewItemsTable
                 items={items}
                 totalItems={totalItems}
                 currentPage={currentPage}
                 pageSize={pageSize}
                 loading={isItemsLoading}
                 onPageChange={setCurrentPage}
                 onViewDiff={(item) => setDiffModalState({isOpen: true, item})}
                 apiKey={apiKey}
               />
             </div>
          </CardContent>
        </Card>

        {diffModalState.item && (
          <ReviewDiffModal
            isOpen={diffModalState.isOpen}
            onClose={() => setDiffModalState({isOpen: false, item: null})}
            original={diffModalState.item.original_response}
            correction={diffModalState.item.result_detail}
            requestId={diffModalState.item.request_id}
          />
        )}

      </div>
    </div>
  );
};

export default ReviewDetail;
